import { Component, computed, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { UserPermission } from '../../../modules/auth0/models';
import { Auth0Store } from '../../../modules/auth0/store';
import { FreemiumRegistrationFormPopupComponent } from '../../../shared/components/freemium-registration-form-popup/freemium-registration-form-popup.component';
import { TopNavComponent } from '../../../shared/containers/top-nav/top-nav.component';
import { ClickCursorDirective } from '../../../shared/directives/click-cursor.directive';
import { ElementPermissionResolverDirective } from '../../../shared/directives/element-permission-resolver.directive';
import { MiniApps } from '../../../shared/models';
import { RootSearchStore, RootUtilityStore } from '../../../store';
import { SearchComponent } from '../../components/search/search.component';

@Component({
  selector: 'gnx-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss'],
  imports: [TopNavComponent, SearchComponent, ElementPermissionResolverDirective, MatTooltip, ClickCursorDirective],
})
export class HomePageComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private matDialog = inject(MatDialog);

  protected auth0Store = inject(Auth0Store);
  private searchStore = inject(RootSearchStore);
  private destroyRef = inject(DestroyRef);
  UserPermission = UserPermission;
  MiniApps = MiniApps;

  private utilityStore = inject(RootUtilityStore);
  showSearch = computed(() => !environment.hideSearchForNonLoggedIn || this.auth0Store.isLoggedIn());

  ngOnInit() {
    this.matDialog.afterOpened
      .pipe(
        filter((dialog) => dialog.componentInstance instanceof FreemiumRegistrationFormPopupComponent),
        switchMap((dialog) => dialog.afterClosed()),
        map(() => this.auth0Store.userDetails()),
        filter((userDetails) => !!userDetails?.organization),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (localStorage.getItem('open-report')) {
          return this.createCaseFromReport();
        }
        if (localStorage.getItem('open-cma')) {
          return this.createCaseFromCMA();
        }
        if (localStorage.getItem('open-vcf')) {
          return this.createCaseFromVCF();
        }
      });
  }

  ngOnDestroy(): void {
    this.searchStore.clearSearchError();
  }

  createCaseFromVCF() {
    const userDetails = this.auth0Store.userDetails();
    if (userDetails?.organization) {
      localStorage.removeItem('open-vcf');
      this.router.navigate([{ outlets: { dialog: 'new-case' } }]);
      return;
    }
    localStorage.setItem('open-vcf', 'true');
    this.utilityStore.openRegistrationPopup();
  }

  createCaseFromReport() {
    const userDetails = this.auth0Store.userDetails();
    if (userDetails?.organization) {
      localStorage.removeItem('open-report');
      this.router.navigate([{ outlets: { dialog: 'new-case/multiple' } }]);
      return;
    }
    localStorage.setItem('open-report', 'true');
    this.utilityStore.openRegistrationPopup();
  }

  createCaseFromCMA() {
    const userDetails = this.auth0Store.userDetails();
    if (userDetails?.organization) {
      localStorage.removeItem('open-cma');
      this.router.navigate([{ outlets: { dialog: 'new-case/cma' } }]);
      return;
    }
    localStorage.setItem('open-cma', 'true');
    this.utilityStore.openRegistrationPopup();
  }
}
